import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Star, User, Shield, Heart, Share2, ChevronRight, Clock, AlertCircle, AlertTriangle, Mail, MessageSquare, Twitter, Facebook, Check, Copy, X, Edit2, ArrowLeft } from 'lucide-react';
import Button from '../components/ui/Button';
import DatePicker from '../components/ui/DatePicker';
import { GET_TOOL_BY_ID } from '../graphql/queries/getTool';
import { REGISTER_TOOL_VIEW } from '../graphql/mutations/registerToolView';
import { Tool } from '../types';
import { useQuery, useMutation } from '@apollo/client';
import { useFavorites } from '../context/FavoritesContext';
import { useAuth } from '../context/AuthContext';
import UserAvatar from '../components/ui/UserAvatar';
import ToolAvailabilityCalendar from '../components/features/ToolAvailabilityCalendar';
import { getToolConditionLabel } from '../lib/toolCondition';
import toast from 'react-hot-toast';

const ToolDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const { data: toolData } = useQuery(GET_TOOL_BY_ID, {
    variables: { 
      id,
      availabilityStartDate: new Date().toISOString().split('T')[0]
    },
    fetchPolicy: 'network-only',
  });
  const [registerToolView] = useMutation(REGISTER_TOOL_VIEW);
  const [tool, setTool] = useState<Tool | null>(null);
  const isToolDisabled = !!tool?.disabled;
  const {brandName, model, powerDescription, condition, weightGrams, ageMonths, usageInstructions, includedItems, owner, maxAvailableCount} = tool || {};
  const displayedCondition = getToolConditionLabel(condition);
  const {name, about, createdAt} = owner || {};
  const displayedWeight = weightGrams ? `${(weightGrams / 1000).toFixed(1)} kg`.replace('.', ',') : '';
  const displayedAge = ageMonths ? `${(ageMonths / 12).toFixed(1)} år`.replace('.', ',') : '';
  const { isFavorite, toggleFavorite } = useFavorites();
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [dateValidationError, setDateValidationError] = useState<string>('');
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const returnTo = useMemo(() => {
    const returnToParam = new URLSearchParams(location.search).get('returnTo');
    if (!returnToParam) return '/browse';
    return returnToParam.startsWith('/browse') ? returnToParam : '/browse';
  }, [location.search]);

  useEffect(() => {
    if (toolData) {
      setTool(toolData.tool);
      setSelectedImageUrl(toolData.tool.primaryImageUrl);
      
      // Set start and end date from nextAvailableDate
      if (toolData.tool.nextAvailableDate) {
        setStartDate(toolData.tool.nextAvailableDate);
        setEndDate(toolData.tool.nextAvailableDate);
      }

      // Register view if user is not the owner
      if (user && toolData.tool.owner && user.id !== toolData.tool.owner.id) {
        registerToolView({
          variables: { toolId: id }
        }).catch(err => {
          console.error('Failed to register tool view:', err);
        });
      }
    }
  }, [toolData, id, user, registerToolView]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('updated') === 'true') {
      toast.success('Værktøjet er opdateret!');
      // Remove the query parameter from URL
      params.delete('updated');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
    }
  }, [location, navigate]);

  const liked = isFavorite(tool?.id);
  const toggleLike = () => toggleFavorite(tool);

  const calculateDistanceKm = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const earthRadiusKm = 6371;
    const deltaLat = toRad(lat2 - lat1);
    const deltaLng = toRad(lng2 - lng1);
    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  };

  const getToolCoordinates = (toolItem: Tool) => {
    if (
      typeof toolItem.locationArea?.latitude === 'number' &&
      typeof toolItem.locationArea?.longitude === 'number'
    ) {
      return {
        latitude: toolItem.locationArea.latitude,
        longitude: toolItem.locationArea.longitude,
      };
    }

    if (typeof toolItem.latitude === 'number' && typeof toolItem.longitude === 'number') {
      return {
        latitude: toolItem.latitude,
        longitude: toolItem.longitude,
      };
    }

    return null;
  };

  useEffect(() => {
    if (!navigator.geolocation || !navigator.permissions) {
      return;
    }

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((permissionStatus) => {
        if (permissionStatus.state !== 'granted') {
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          () => {
            // Stay silent if position cannot be read.
          }
        );
      })
      .catch(() => {
        // Ignore permission API errors and skip distance display.
      });
  }, []);

  const distanceToToolKm = useMemo(() => {
    if (!tool || !userLocation) return null;

    const toolCoordinates = getToolCoordinates(tool);
    if (!toolCoordinates) return null;

    return calculateDistanceKm(
      userLocation.latitude,
      userLocation.longitude,
      toolCoordinates.latitude,
      toolCoordinates.longitude
    );
  }, [tool, userLocation]);

  const distanceLabel = useMemo(() => {
    if (distanceToToolKm === null) return null;
    if (distanceToToolKm < 1) return `${Math.round(distanceToToolKm * 1000)} m fra dig`;
    return `${distanceToToolKm.toFixed(1).replace('.', ',')} km fra dig`;
  }, [distanceToToolKm]);

  // Calculate all disabled dates for date pickers
  const disabledDates = useMemo(() => {
    if (!toolData?.tool) return [];
    
    const disabled: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Generate dates for next 2 years to check
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 2);
    
    const availabilityPeriods = toolData.tool.toolAvailabilityPeriods || [];
    const bookings = toolData.tool.relevantToolBookings || [];
    
    const currentDate = new Date(today);
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      
      // Find ALL matching availability periods for this date
      const matchingPeriods = availabilityPeriods.filter((period: any) => {
        const periodStartStr = period.startDate;
        const periodEndStr = period.endDate;
        
        return dateStr >= periodStartStr && (!periodEndStr || dateStr <= periodEndStr);
      });
      
      // Check if there are any bookings for this date
      const hasBooking = bookings.some((booking: any) => {
        if (booking.status === 'CANCELLED') return false;
        return dateStr >= booking.fromDate && dateStr <= booking.toDate;
      });
      
      // Determine if date should be disabled
      let shouldDisable = false;
      
      if (hasBooking) {
        // If there's a booking, always disable
        shouldDisable = true;
      } else if (matchingPeriods.length === 0) {
        // No availability period defined = disable
        shouldDisable = true;
      } else {
        // Multiple periods may match - prioritize most specific
        // Specific periods (with endDate) override open-ended ones
        const specificPeriods = matchingPeriods.filter((p: any) => p.endDate);
        const periodToUse = specificPeriods.length > 0 
          ? specificPeriods[specificPeriods.length - 1]
          : matchingPeriods[matchingPeriods.length - 1];
        
        // Disable if the chosen period is not available
        shouldDisable = !periodToUse.available;
      }
      
      if (shouldDisable) {
        disabled.push(dateStr);
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return disabled;
  }, [toolData]);

  // Check if date range is valid
  const validateDateRange = (start: string, end: string): string => {
    if (!start || !end) return '';
    
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    if (startDate > endDate) {
      return 'Slutdato skal være efter startdato';
    }
    
    // Check each day in the range
    const currentDate = new Date(start);
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      if (disabledDates.includes(dateStr)) {
        return `Værktøjet er ikke tilgængeligt den ${currentDate.toLocaleDateString('da-DK')}`;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return '';
  };

  // Handle date changes with validation
  const handleStartDateChange = (newStartDate: string) => {
    const shouldAdjustEndDate =
      !!endDate &&
      !!newStartDate &&
      new Date(newStartDate).getTime() > new Date(endDate).getTime();

    const adjustedEndDate = shouldAdjustEndDate ? newStartDate : endDate;

    setStartDate(newStartDate);
    if (shouldAdjustEndDate) {
      setEndDate(newStartDate);
    }

    const error = validateDateRange(newStartDate, adjustedEndDate);
    setDateValidationError(error);
  };

  const handleEndDateChange = (newEndDate: string) => {
    setEndDate(newEndDate);
    const error = validateDateRange(startDate, newEndDate);
    setDateValidationError(error);
  };
  
  // Calculate total price
  const pricePerDay = (tool?.pricePerDayCents ?? 0) / 100.0;
  const days = startDate && endDate 
    ? Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
    : 0;
  const totalPrice = days * pricePerDay * quantity;
  
  // Share functionality
  const toolUrl = `${window.location.origin}/tool/${tool?.id}`;
  const shareTitle = `Se det her værktøj ${tool?.name} på DoMore`;
  const shareDescription = `${tool?.description.substring(0, 100)}...`;
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(toolUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = toolUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };
  
  const shareOptions = [
    {
      name: 'Kopiér link',
      icon: copySuccess ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />,
      action: handleCopyLink,
      color: copySuccess ? 'text-green-600' : 'text-gray-600',
      bgColor: copySuccess ? 'bg-green-50 hover:bg-green-100' : 'bg-gray-50 hover:bg-gray-100',
    },
    {
      name: 'Facebook',
      icon: <Facebook className="h-5 w-5" />,
      action: () => {
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(toolUrl)}&quote=${encodeURIComponent(shareTitle)}`;
        window.open(url, '_blank', 'width=600,height=400');
      },
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 hover:bg-blue-100',
    },
    {
      name: 'Twitter',
      icon: <Twitter className="h-5 w-5" />,
      action: () => {
        const text = `${shareTitle} - ${shareDescription}`;
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(toolUrl)}`;
        window.open(url, '_blank', 'width=600,height=400');
      },
      color: 'text-blue-400',
      bgColor: 'bg-blue-50 hover:bg-blue-100',
    },
    {
      name: 'Email',
      icon: <Mail className="h-5 w-5" />,
      action: () => {
        const subject = encodeURIComponent(shareTitle);
        const body = encodeURIComponent(`Jeg har fundet dette gode værktøj på DoMore, som du måske er interesseret i:\n\n${tool?.name}\n${shareDescription}\n\nTjek det ud: ${toolUrl}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      },
      color: 'text-gray-600',
      bgColor: 'bg-gray-50 hover:bg-gray-100',
    },
    {
      name: 'WhatsApp',
      icon: <MessageSquare className="h-5 w-5" />,
      action: () => {
        const text = `${shareTitle}\n${shareDescription}\n${toolUrl}`;
        const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
      },
      color: 'text-green-500',
      bgColor: 'bg-green-50 hover:bg-green-100',
    },
  ];


  // Included items
  const icludedListItems = (includedItems || []).map((ii) => {
    return (
      <li key={ii}>{ii}</li>
    )
  })
  if (!tool) {
    return null;
  }
  
  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {/* Navigation Breadcrumbs */}
      <div className="flex items-center text-sm text-gray-500 mb-4">
        <Link to="/" className="hover:text-gray-700">Forside</Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <Link to="/browse" className="hover:text-gray-700">Find værktøj</Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="text-gray-900">{tool.name}</span>
      </div>

      <div className="mb-6">
        <Link to={returnTo}>
          <Button variant="outline" icon={<ArrowLeft className="h-4 w-4 mr-2" />}>
            Tilbage til søgning
          </Button>
        </Link>
      </div>

      {isToolDisabled && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-700 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Værktøjet er deaktiveret</p>
              <p className="text-sm text-amber-800 mt-1">
                Dette værktøj kan ikke bookes og er skjult fra søgning, indtil ejeren aktiverer det igen.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Images */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Main Image */}
            <div className="aspect-w-16 aspect-h-9 bg-gray-200 relative">
              {isToolDisabled && (
                <div className="absolute top-3 left-3 z-10 inline-flex items-center rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                  Deaktiveret
                </div>
              )}
              <img 
                src={selectedImageUrl || tool.primaryImageUrl} 
                alt={tool.name} 
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Thumbnail Images */}
            {tool.images && tool.images.length > 0 && (
              <div className="grid grid-cols-4 gap-2 p-2">
                {tool.images.map((image) => (
                  <div 
                    key={image.id} 
                    onClick={() => setSelectedImageUrl(image.url)}
                    className={`aspect-w-1 aspect-h-1 bg-gray-100 rounded overflow-hidden cursor-pointer hover:opacity-75 transition-opacity ${
                      selectedImageUrl === image.url ? 'ring-2 ring-green-600' : ''
                    }`}
                  >
                    <img 
                      src={image.url} 
                      alt={`${tool.name} billede ${image.position}`} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Tool Information */}
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <div className="flex space-x-8">
                <span className="pb-4 -mb-px text-sm font-medium text-green-600 border-b-2 border-green-600">
                  Beskrivelse
                </span>
                <Link
                  to={`/tool/${id}/reviews`}
                  className="pb-4 -mb-px text-sm font-medium flex items-center text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300"
                >
                  Bedømmelser
                  <span className="ml-2 bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs">
                    {tool?.reviewCount}
                  </span>
                </Link>
              </div>
            </div>
            
            {/* Tab Content */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Om værktøjet</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700">{tool.description}</p>
                
                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Specifikationer</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="w-24 font-medium">Mærke:</span>
                    <span>{brandName}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-24 font-medium">Model:</span>
                    <span>{model}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-24 font-medium">Effekt:</span>
                    <span>{powerDescription}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-24 font-medium">Vægt:</span>
                    <span>{displayedWeight}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-24 font-medium">Stand:</span>
                    <span>{displayedCondition}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-24 font-medium">Alder:</span>
                    <span>{displayedAge}</span>
                  </li>
                </ul>
                
                {icludedListItems.length > 0 &&
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Hvad der er inkluderet</h3>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      {icludedListItems}
                    </ul>
                  </>
                }
                
                <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Brugsanvisning</h3>
                <p className="text-gray-700">
                  {usageInstructions}
                </p>
              </div>
            </div>
          </div>
          
          {/* Owner Information */}
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Om ejeren</h2>
            <div className="flex items-center">
              <UserAvatar user={owner}/>
              <div className="ml-5">
                <h3 className="font-medium text-gray-900">{name}</h3>
                <div className="flex items-center text-sm text-gray-500">
                  {!!createdAt && 
                    <span className="flex items-center mr-3">
                      <Clock className="h-4 w-4 mr-1" />
                      Medlem siden {new Date(createdAt).getFullYear()}
                    </span>
                  }
                  <span className="flex items-center">
                    <Star className="h-4 w-4 text-yellow-500 mr-1 fill-current" />
                    {owner?.ratingAverage ? owner.ratingAverage.toFixed(1) : 0} ({owner?.reviewCount || 0} bedømmelser)
                  </span>
                </div>
              </div>
            </div>
            <p className="mt-4 text-gray-700">
             {about}
            </p>
            <div className="mt-4">
              <Link to={`/profile/${tool.owner?.id || 'user1'}`}>
                <Button variant="outline" icon={<User className="h-4 w-4 mr-2" />}>
                  Vis profil
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Right Column - Booking */}
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{tool.name}</h1>
                  {isToolDisabled && (
                    <div className="mt-2 inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                      Deaktiveret
                    </div>
                  )}
                  <div className="mt-1 flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                    <span>{tool.city}{tool.postalCode ? `, ${tool.postalCode}` : ''}</span>
                  </div>
                  {distanceLabel && (
                    <div className="mt-1 text-sm text-gray-600">Ca. {distanceLabel}</div>
                  )}
                </div>
                {tool.isOwnTool ? (
                  <Link to={`/edit-tool/${tool.id}`}>
                    <Button variant="outline" size="sm" icon={<Edit2 className="h-4 w-4" />}>
                      Rediger
                    </Button>
                  </Link>
                ) : (
                  <button 
                    onClick={toggleLike}
                    className={`p-2 rounded-full ${
                      liked ? 'text-red-500' : 'text-gray-400 hover:text-gray-500'
                    }`}
                    aria-label="Add to favorites"
                  >
                    <Heart className={`h-5 w-5 ${liked ? 'fill-current' : ''}`} />
                  </button>
                )}
              </div>
              
              <div className="mt-4 flex items-center justify-between border-t border-b border-gray-200 py-4">
                <div className="text-2xl font-bold text-gray-900">{pricePerDay} kr. <span className="text-sm font-normal text-gray-500">/ dag</span></div>
                <div className="flex items-center">
                  <Star className="h-4 w-4 text-yellow-500 fill-current" />
                  <span className="ml-1 font-medium">{tool?.ratingAverage ? tool?.ratingAverage.toFixed(1) : 0}</span>
                  <Link to={`/tool/${id}/reviews`} className="ml-1 text-gray-500 hover:text-green-700 hover:underline">
                    ({tool?.reviewCount || 0} bedømmelser)
                  </Link>
                </div>
              </div>
              
              <div className="mt-6">
                {!tool.isOwnTool && user &&
                  <>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Lån dette værktøj</h3>

                    {isToolDisabled && (
                      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start">
                        <AlertTriangle className="h-5 w-5 text-amber-700 mr-2 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">
                          Dette værktøj er deaktiveret af ejeren og kan ikke bookes lige nu.
                        </p>
                      </div>
                    )}

                    {!isToolDisabled && (
                      <>
                  
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <DatePicker
                        id="start-date"
                        label="Startdato"
                        value={startDate}
                        onChange={handleStartDateChange}
                        minDate={new Date().toISOString().split('T')[0]}
                        disabledDates={disabledDates}
                        error={!!dateValidationError}
                      />
                      <DatePicker
                        id="end-date"
                        label="Slutdato"
                        value={endDate}
                        onChange={handleEndDateChange}
                        minDate={startDate || new Date().toISOString().split('T')[0]}
                        disabledDates={disabledDates}
                        error={!!dateValidationError}
                      />
                    </div>

                    {dateValidationError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{dateValidationError}</p>
                      </div>
                    )}

                    <div className="mb-4">
                      <button
                        type="button"
                        onClick={() => setShowAvailabilityModal(true)}
                        className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Vis tilgængelighed
                      </button>
                    </div>
                  
                    {!!maxAvailableCount && maxAvailableCount > 1 &&
                      <div className="mb-6">
                        <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
                          Antal
                        </label>
                        <select
                          id="quantity"
                          value={quantity}
                          onChange={(e) => setQuantity(parseInt(e.target.value))}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                        >
                          {[1, 2, 3, 4, 5].map((num) => (
                            <option key={num} value={num}>
                              {num}
                            </option>
                          ))}
                        </select>
                      </div>
                    }
                  
                    {days > 0 && (
                      <div className="space-y-2 mb-6">
                        <div className="flex justify-between text-sm">
                          {quantity > 1 ?
                            <span className="text-gray-600">{pricePerDay} kr. × {days} dag(e) × {quantity}</span>
                          :
                            <span className="text-gray-600">{pricePerDay} kr. × {days} dag(e)</span>
                        }
                          <span className="font-medium">{totalPrice.toFixed(2)} kr.</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-200 font-medium">
                          <span>Total</span>
                          <span>{totalPrice.toFixed(2)} kr.</span>
                        </div>
                      </div>
                    )}

                    <Link to={`/tool/${id}/book?startDate=${startDate}&endDate=${endDate}`}>
                      <Button 
                        variant={!startDate || !endDate || days <= 0 || dateValidationError ? 'outline' : 'primary'}
                        fullWidth
                        disabled={!startDate || !endDate || days <= 0 || !!dateValidationError}
                      >
                        Anmod om bookning
                      </Button>
                    </Link>
                      </>
                    )}
                  </>}

                <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="flex items-center text-green-600 hover:text-green-700 transition-colors"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    <span>Del dette værktøj</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Tool Policies */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Værktøjspolitikker</h3>
              
              <div className="space-y-4">
                <div className="flex">
                  <Shield className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Forsikringsdækning</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Alle udlån inkluderer en grundlæggende forsikring for skader op til 5.000 kr.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <Calendar className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">Afbestillingspolitik</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Begge parter kan annullere booking op til 12 timer før afhentning.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <Clock className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900">For sene returneringer</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Der kan pålægges en ekstra dagspris for sene returneringer.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Del værktøj</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Tool Preview */}
            <div className="flex items-center mb-6 p-3 bg-gray-50 rounded-lg">
              <img
                src={tool.primaryImageUrl}
                alt={tool.name}
                className="h-12 w-12 rounded object-cover mr-3"
              />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 truncate">{tool.name}</h4>
                <p className="text-sm text-gray-500">{pricePerDay} kr. / dag</p>
              </div>
            </div>
            
            {/* Share Options */}
            <div className="grid grid-cols-2 gap-3">
              {shareOptions.map((option) => (
                <button
                  key={option.name}
                  onClick={option.action}
                  className={`flex items-center justify-center p-3 rounded-lg border border-gray-200 transition-colors ${option.bgColor}`}
                >
                  <span className={`mr-2 ${option.color}`}>
                    {option.icon}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {option.name === 'Kopier link' && copySuccess ? 'Kopieret!' : option.name}
                  </span>
                </button>
              ))}
            </div>
            
            {/* URL Display */}
            <div className="mt-6 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Værktøjs URL:</p>
              <p className="text-sm text-gray-900 break-all">{toolUrl}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Backdrop Click Handler */}
      {showShareModal && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowShareModal(false)}
        />
      )}

      {/* Availability Calendar Modal */}
      {showAvailabilityModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Tilgængelighed</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Se hvornår værktøjet er tilgængeligt til lån
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAvailabilityModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <ToolAvailabilityCalendar
                  periods={toolData?.tool?.toolAvailabilityPeriods || []}
                  onChange={() => {}} // Read-only mode
                  toolId={id}
                  readOnly={true}
                />
                
                {/* Bookings Info */}
                {toolData?.tool?.relevantToolBookings && toolData.tool.relevantToolBookings.length > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Aktive bookinger</h4>
                    <ul className="space-y-2">
                      {toolData.tool.relevantToolBookings
                        .filter((booking: any) => booking.status !== 'CANCELLED')
                        .map((booking: any) => (
                          <li key={booking.id} className="text-sm text-blue-700">
                            {new Date(booking.fromDate).toLocaleDateString('da-DK')} - {new Date(booking.toDate).toLocaleDateString('da-DK')}
                            {' '}({booking.status})
                          </li>
                        ))
                      }
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Modal Backdrop Click Handler */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowAvailabilityModal(false)}
          />
        </>
      )}

    </div>
  );
};

export default ToolDetail;